import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "react-toastify";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import { registerService, sendRegisterVerificationService, verifyRegisterCodeService, resendRegisterVerificationService } from "../../services/authService";
import Logo from "../../assets/Images/logo.svg";
import GooglePlacesAutocomplete from "react-google-autocomplete";
import { Google_map_Key } from "../../api/url";

const RESEND_COOLDOWN_SECONDS = 60;

// ─── Shared micro-components ──────────────────────────────────────────────────

const EyeIcon = ({ open }) =>
  open ? (
    <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8}
        d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
    </svg>
  ) : (
    <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8}
        d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
    </svg>
  );

const Spinner = () => (
  <svg style={{ width: 15, height: 15, animation: "spin 1s linear infinite" }} fill="none" viewBox="0 0 24 24">
    <style>{`@keyframes spin{from{transform:rotate(0deg)}to{transform:rotate(360deg)}}`}</style>
    <circle style={{ opacity: 0.25 }} cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
    <path style={{ opacity: 0.75 }} fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
  </svg>
);

// ─── Step progress indicator ──────────────────────────────────────────────────

const STEPS = [
  { n: 1, label: "Account" },
  { n: 2, label: "Personal" },
  { n: 3, label: "Verify" },
];

const StepIndicator = ({ step }) => (
  <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 0, padding: "18px 0 6px" }}>
    {STEPS.map(({ n, label }, idx) => {
      const done   = step > n;
      const active = step === n;
      return (
        <div key={n} style={{ display: "flex", alignItems: "center" }}>
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 5 }}>
            <div style={{
              width: 30, height: 30, borderRadius: "50%",
              background: done ? "#FEBA06" : active ? "#FEBA06" : "#f3f4f6",
              color: done || active ? "#111827" : "#9ca3af",
              display: "flex", alignItems: "center", justifyContent: "center",
              fontSize: 12, fontWeight: 800,
              boxShadow: active ? "0 0 0 3px rgba(254,186,6,0.25)" : "none",
              transition: "all 0.25s",
            }}>
              {done
                ? <svg width="13" height="13" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" /></svg>
                : n}
            </div>
            <span style={{
              fontSize: 9, fontWeight: 700,
              color: done || active ? "#FEBA06" : "#9ca3af",
              textTransform: "uppercase", letterSpacing: "0.07em",
            }}>{label}</span>
          </div>
          {idx < STEPS.length - 1 && (
            <div style={{
              width: 52, height: 2,
              background: done ? "#FEBA06" : "#e5e7eb",
              margin: "0 6px 16px",
              transition: "background 0.25s",
            }} />
          )}
        </div>
      );
    })}
  </div>
);

// ─── Phone input style helpers ────────────────────────────────────────────────

const phoneContainerStyle = { width: "100%" };

const phoneInputStyle = (err) => ({
  width: "100%",
  border: `1.5px solid ${err ? "#dc2626" : "#e5e7eb"}`,
  borderRadius: 8,
  padding: "10px 12px 10px 52px",
  fontSize: 13,
  color: "#161412",
  background: err ? "#fef2f2" : "#f9fafb",
  outline: "none",
  fontFamily: "inherit",
  height: "auto",
  boxSizing: "border-box",
});

const phoneButtonStyle = (err) => ({
  border: `1.5px solid ${err ? "#dc2626" : "#e5e7eb"}`,
  borderRight: "1px solid #e5e7eb",
  borderRadius: "8px 0 0 8px",
  background: err ? "#fef2f2" : "#f9fafb",
});

// ─── Shared form-field style tokens ──────────────────────────────────────────

const mLbl = {
  display: "block", fontSize: 11, fontWeight: 700,
  color: "#161412", letterSpacing: "0.07em",
  textTransform: "uppercase", marginBottom: 6,
};
const optTag = { fontSize: 9, color: "#9ca3af", fontWeight: 400, textTransform: "none", letterSpacing: 0 };
const errMsg = { margin: "5px 0 0", fontSize: 11, color: "#dc2626", display: "flex", alignItems: "center", gap: 4 };

const FieldInput = ({ label, optional, name, value, onChange, onFocus, onBlur, error, placeholder, type = "text", autoComplete, children }) => (
  <div>
    <label style={mLbl}>{label}{optional && <span style={optTag}> (optional)</span>}</label>
    {children ?? (
      <input
        type={type} name={name} value={value}
        onChange={onChange} onFocus={onFocus} onBlur={onBlur}
        autoComplete={autoComplete} placeholder={placeholder}
        style={{
          width: "100%", border: `1.5px solid ${error ? "#dc2626" : "#e5e7eb"}`,
          borderRadius: 8, padding: "10px 12px", fontSize: 13, color: "#161412",
          background: error ? "#fef2f2" : "#f9fafb",
          outline: "none", boxSizing: "border-box", fontFamily: "inherit",
        }}
      />
    )}
    {error && <p style={errMsg}>⚠ {error}</p>}
  </div>
);

// ─── Password strength indicator ─────────────────────────────────────────────

const getStrength = (pwd) => {
  let score = 0;
  if (pwd.length >= 8)              score++;
  if (/[A-Z]/.test(pwd))            score++;
  if (/[0-9]/.test(pwd))            score++;
  if (/[^A-Za-z0-9]/.test(pwd))    score++;
  return score; // 0-4
};

const StrengthBar = ({ password }) => {
  if (!password) return null;
  const score = getStrength(password);
  const labels = ["", "Weak", "Fair", "Good", "Strong"];
  const colors = ["", "#ef4444", "#f59e0b", "#3b82f6", "#22c55e"];
  return (
    <div style={{ marginTop: 6 }}>
      <div style={{ display: "flex", gap: 3, marginBottom: 3 }}>
        {[1, 2, 3, 4].map(i => (
          <div key={i} style={{
            flex: 1, height: 3, borderRadius: 99,
            background: i <= score ? colors[score] : "#e5e7eb",
            transition: "background 0.2s",
          }} />
        ))}
      </div>
      <p style={{ fontSize: 10, color: colors[score], fontWeight: 600, margin: 0 }}>{labels[score]}</p>
    </div>
  );
};

// ─── Left dark panel (full-page only) ────────────────────────────────────────

const LEFT_COPY = {
  1: { title: "Create Your\nAccount",  sub: "Start with your login credentials. It only takes a minute." },
  2: { title: "Almost\nThere",         sub: "Just a few personal details and you're ready to go." },
  3: { title: "Verify Your\nEmail",    sub: "We sent a 6-digit code to your inbox. Enter it to finish signing up." },
};

const LeftPanel = ({ step, navigate, toLogin }) => {
  const copy = LEFT_COPY[step] || LEFT_COPY[1];
  return (
    <div style={{
      width: 268, flexShrink: 0,
      display: "flex", flexDirection: "column", justifyContent: "space-between",
      padding: 28, position: "relative", overflow: "hidden",
      background: "linear-gradient(160deg,#1a1710 0%,#201d14 55%,#2e2718 100%)",
    }}>
      {/* Glow blobs */}
      <div style={{ position: "absolute", width: 200, height: 200, borderRadius: "50%", background: "#FEBA06", opacity: 0.1, top: -55, right: -55, filter: "blur(40px)", pointerEvents: "none" }} />
      <div style={{ position: "absolute", width: 130, height: 130, borderRadius: "50%", background: "#FEBA06", opacity: 0.06, bottom: -35, left: -35, filter: "blur(32px)", pointerEvents: "none" }} />
      <div style={{ position: "absolute", width: 110, height: 110, borderRadius: "50%", border: "1px solid rgba(255,255,255,0.06)", top: "30%", right: -20, pointerEvents: "none" }} />

      {/* Logo */}
      <div style={{ position: "relative", zIndex: 1 }}>
        <img src={Logo} alt="Paving Market" onClick={() => navigate("/")}
          style={{ height: 28, width: "auto", cursor: "pointer", opacity: 0.9 }} />
      </div>

      {/* Copy */}
      <div style={{ position: "relative", zIndex: 1 }}>
        <div style={{ width: 26, height: 3, background: "#FEBA06", borderRadius: 99, marginBottom: 16 }} />
        <div style={{ fontSize: 28, fontWeight: 800, color: "#ffffff", lineHeight: 1.15, letterSpacing: "-0.02em", whiteSpace: "pre-line" }}>
          {copy.title}
        </div>
        <p style={{ fontSize: 11, color: "#9ca3af", lineHeight: 1.55, marginTop: 8, maxWidth: 190 }}>
          {copy.sub}
        </p>
        {step < 4 && (
          <div style={{ marginTop: 20 }}>
            {[{ n: "1", t: "Create your account" }, { n: "2", t: "Browse equipment" }, { n: "3", t: "Start buying & selling" }].map(({ n, t }) => (
              <div key={n} style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
                <div style={{ width: 18, height: 18, borderRadius: 5, background: "rgba(254,186,6,0.15)", border: "1px solid rgba(254,186,6,0.25)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                  <span style={{ fontSize: 8, fontWeight: 800, color: "#FEBA06" }}>{n}</span>
                </div>
                <span style={{ fontSize: 11, color: "#9ca3af" }}>{t}</span>
              </div>
            ))}
          </div>
        )}
        <div style={{ marginTop: 20 }}>
          <p style={{ fontSize: 10, color: "#4b5563", marginBottom: 8 }}>Already have an account?</p>
          <Link to="/login" onClick={toLogin} style={{ display: "inline-flex", alignItems: "center", gap: 6, border: "1px solid rgba(255,255,255,0.2)", color: "#ffffff", fontSize: 11, fontWeight: 600, padding: "8px 16px", borderRadius: 9, textDecoration: "none" }}>
            Log in
            <svg width="11" height="11" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </Link>
        </div>
      </div>

      <p style={{ position: "relative", zIndex: 1, fontSize: 9, color: "#374151" }}>
        © {new Date().getFullYear()} Paving Market
      </p>
    </div>
  );
};

// ═════════════════════════════════════════════════════════════════════════════
// Main Register component
// ═════════════════════════════════════════════════════════════════════════════

/**
 * isModal  – when true renders compact modal card (no full-page background)
 * onClose  – called to close the modal
 * onSwitch – called with "login" or "forgot-password" to switch modal view
 */
const Register = ({ isModal = false, onClose, onSwitch }) => {
  const navigate = useNavigate();

  // ── Wizard step ─────────────────────────────────────────────────────────
  const [step, setStep] = useState(1);

  // ── Form state ───────────────────────────────────────────────────────────
  const [form, setForm] = useState({
    email: "", password: "", confirmPassword: "",
    firstName: "", lastName: "",
    address: "", city: "", state: "", zipCode: "", country: "",
    receiveMarketingEmails: false,
  });

  // Phone inputs managed separately (react-phone-input-2 pattern)
  const [phone,              setPhone]              = useState("");
  const [dialCode,           setDialCode]           = useState("1");
  const [secondaryPhone,     setSecondaryPhone]     = useState("");
  const [secondaryDialCode,  setSecondaryDialCode]  = useState("1");

  const [showPwd,  setShowPwd]  = useState(false);
  const [showCfm,  setShowCfm]  = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const [error,    setError]    = useState("");
  const [loading,  setLoading]  = useState(false);

  // Email OTP verification state (step 3)
  const [otpCode,           setOtpCode]           = useState("");
  const [otpVerified,       setOtpVerified]       = useState(false);
  const [otpLoading,        setOtpLoading]        = useState(false);
  const [otpError,          setOtpError]          = useState("");
  const [verificationToken, setVerificationToken] = useState("");
  const [otpUserId,         setOtpUserId]         = useState(null);
  const [resendCooldown,    setResendCooldown]    = useState(0);
  const [resendLoading,     setResendLoading]     = useState(false);

  // Cooldown ticker — counts down from RESEND_COOLDOWN_SECONDS to 0
  useEffect(() => {
    if (resendCooldown <= 0) return;
    const t = setTimeout(() => setResendCooldown((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [resendCooldown]);

  // ── Input handler ────────────────────────────────────────────────────────
  const ch = (e) => {
    const { name, value, type, checked } = e.target;
    setForm(p => ({ ...p, [name]: type === "checkbox" ? checked : value }));
    if (type !== "checkbox") setFieldErrors(p => ({ ...p, [name]: "" }));
    setError("");
  };

  // ── Step 1 validation ────────────────────────────────────────────────────
  const validateStep1 = () => {
    const errs = {};
    if (!form.email.trim())
      errs.email = "Email is required.";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim()))
      errs.email = "Enter a valid email address.";

    if (!form.password)
      errs.password = "Password is required.";
    else if (form.password.length < 8)
      errs.password = "Password must be at least 8 characters.";
    else if (!/[A-Z]/.test(form.password))
      errs.password = "Password must contain at least one uppercase letter.";
    else if (!/[0-9]/.test(form.password))
      errs.password = "Password must contain at least one number.";
    else if (!/[^A-Za-z0-9]/.test(form.password))
      errs.password = "Password must contain at least one special character.";

    if (!form.confirmPassword)
      errs.confirmPassword = "Please confirm your password.";
    else if (form.password !== form.confirmPassword)
      errs.confirmPassword = "Passwords do not match.";

    setFieldErrors(errs);
    return Object.keys(errs).length === 0;
  };

  // ── Step 2 validation ────────────────────────────────────────────────────
  const validateStep2 = () => {
    const errs = {};
    if (!form.firstName.trim()) errs.firstName = "First name is required.";
    if (!form.lastName.trim())  errs.lastName  = "Last name is required.";
    if (!phone || phone.length <= dialCode.length) {
      errs.phone = "Phone number is required.";
    } else if ((phone.slice(dialCode.length).replace(/\D/g, "")).length < 7) {
      errs.phone = "Please enter a valid phone number.";
    }
    // Secondary phone is optional, but if the user typed anything past the
    // dial code it must be a valid number — same threshold as the primary.
    if (secondaryPhone && secondaryPhone.length > secondaryDialCode.length) {
      if ((secondaryPhone.slice(secondaryDialCode.length).replace(/\D/g, "")).length < 7) {
        errs.secondaryPhone = "Please enter a valid phone number.";
      }
    }
    setFieldErrors(errs);
    return Object.keys(errs).length === 0;
  };

  // ── Reset OTP state (used when leaving verify step or restarting flow) ──
  const resetOtpState = () => {
    setOtpCode("");
    setOtpVerified(false);
    setOtpError("");
    setVerificationToken("");
    setOtpUserId(null);
    setResendCooldown(0);
  };

  // ── Next / Back ──────────────────────────────────────────────────────────
  const handleNext = () => {
    if (step === 1 && validateStep1()) { setStep(2); setError(""); }
  };

  const handleBack = () => {
    setError("");
    // Leaving the verify step clears OTP state so re-submitting issues a fresh code
    if (step === 3) resetOtpState();
    setStep(s => Math.max(1, s - 1));
  };

  // ── Google Places address auto-fill ─────────────────────────────────────
  const handlePlaceSelect = (place) => {
    const components = place?.address_components || [];
    const get = (types) => {
      const c = components.find((x) => x.types.some((t) => types.includes(t)));
      return c ? c.long_name : "";
    };
    const getShort = (types) => {
      const c = components.find((x) => x.types.some((t) => types.includes(t)));
      return c ? c.short_name : "";
    };
    setForm((p) => ({
      ...p,
      address: place?.formatted_address || p.address,
      city:    get(["locality", "administrative_area_level_2"]),
      state:   get(["administrative_area_level_1"]),
      zipCode: get(["postal_code"]),
      country: get(["country"]),
    }));
    setFieldErrors((p) => ({ ...p, address: "", city: "", state: "", zipCode: "", country: "" }));
  };

  // ── Submit — sends OTP then advances to the Verify step ─────────────────
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateStep2()) return;
    setLoading(true);
    setError("");
    try {
      const res = await sendRegisterVerificationService(form.email.trim());
      const uid = res?.data?.userId ?? res?.data?.userGuid ?? null;
      setOtpUserId(uid);
      setOtpCode("");
      setOtpVerified(false);
      setOtpError("");
      setVerificationToken("");
      setResendCooldown(RESEND_COOLDOWN_SECONDS);
      setStep(3);
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || "Failed to send verification code. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // ── Resend OTP — uses /auth/register/verification/resend with userId ────
  const handleResendOtp = async () => {
    if (resendCooldown > 0 || resendLoading || otpVerified) return;
    setResendLoading(true);
    setOtpError("");
    try {
      const res = await resendRegisterVerificationService({
        userId: otpUserId,
        email: form.email.trim(),
      });
      // Refresh userId in case the resend endpoint returns a new one
      const uid = res?.data?.userId ?? res?.data?.userGuid ?? otpUserId;
      setOtpUserId(uid);
      setOtpCode("");
      setResendCooldown(RESEND_COOLDOWN_SECONDS);
      toast.success("Verification code resent.");
    } catch (err) {
      setOtpError(err?.response?.data?.message || err?.message || "Failed to resend verification code. Please try again.");
    } finally {
      setResendLoading(false);
    }
  };

  // ── Verify OTP — stores verificationToken from response ─────────────────
  const handleVerifyOtp = async () => {
    if (!otpCode.trim()) { setOtpError("Please enter the verification code."); return; }
    setOtpLoading(true);
    setOtpError("");
    try {
      const res = await verifyRegisterCodeService({ email: form.email.trim(), code: otpCode.trim() });
      setVerificationToken(res?.data?.verificationToken || "");
      setOtpVerified(true);
    } catch (err) {
      setOtpError(err?.response?.data?.message || err?.message || "Invalid verification code. Please try again.");
    } finally {
      setOtpLoading(false);
    }
  };

  // ── Continue — calls registration API with EmailVerificationToken ────────
  const handleContinue = async () => {
    setLoading(true);
    setOtpError("");
    try {
      const primaryNumber   = phone.slice(dialCode.length).trim();
      const secondaryNumber = secondaryPhone ? secondaryPhone.slice(secondaryDialCode.length).trim() : undefined;
      await registerService({
        email:                 form.email.trim(),
        password:              form.password,
        confirmPassword:       form.confirmPassword,
        firstName:             form.firstName.trim(),
        lastName:              form.lastName.trim(),
        phoneNumber:           primaryNumber,
        countryCode:           dialCode || undefined,
        secondaryPhoneNumber:  secondaryNumber         || undefined,
        secondaryCountryCode:  secondaryNumber ? secondaryDialCode : undefined,
        address:  form.address.trim()  || undefined,
        city:     form.city.trim()     || undefined,
        state:    form.state.trim()    || undefined,
        zipCode:  form.zipCode.trim()  || undefined,
        country:  form.country.trim()  || undefined,
        receiveMarketingEmails: form.receiveMarketingEmails || undefined,
        emailVerificationToken: verificationToken || undefined,
      });
      toast.success("Account created! Please sign in.");
      // Hand the just-entered credentials to the login screen so the user
      // doesn't have to retype them. Lives only in memory (modal state /
      // router location.state), never persisted.
      const prefill = { email: form.email.trim(), password: form.password };
      if (isModal && onSwitch) {
        onSwitch("login", prefill);
      } else {
        navigate("/login", { state: prefill });
      }
    } catch (err) {
      const apiMsg = err?.response?.data?.message || err?.message || "Registration failed. Please try again.";
      const lower = apiMsg.toLowerCase();
      // If the server rejects the phone number (already linked, invalid, etc.),
      // surface the message on the phone field back on step 2 instead of the
      // Verify step — the user needs to edit the phone, not the OTP.
      const isSecondary = lower.includes("secondary");
      const isPhoneError =
        lower.includes("phone") ||
        lower.includes("mobile") ||
        lower.includes("contact number");
      if (isPhoneError) {
        setFieldErrors(p => ({
          ...p,
          [isSecondary ? "secondaryPhone" : "phone"]: apiMsg,
        }));
        setError("");
        setOtpError("");
        setStep(2);
      } else {
        setOtpError(apiMsg);
      }
    } finally {
      setLoading(false);
    }
  };

  const toLogin = isModal && onSwitch
    ? (e) => { e.preventDefault(); onSwitch("login"); }
    : undefined;

  // ── Focus / blur helpers (shared for both modes) ─────────────────────────
  const onFocus = (name) => (e) => {
    e.target.style.borderColor = fieldErrors[name] ? "#dc2626" : "#FEBA06";
    e.target.style.background  = "#fff";
  };
  const onBlur = (name) => (e) => {
    e.target.style.borderColor = fieldErrors[name] ? "#dc2626" : "#e5e7eb";
    e.target.style.background  = fieldErrors[name] ? "#fef2f2" : "#f9fafb";
  };

  // ── Verify step (step 3) content — rendered inline by both modes ─────────
  const verifyStepContent = (
    <div style={{ maxWidth: 420, margin: "0 auto" }}>
      {/* Header */}
      <div style={{ textAlign: "center", marginBottom: 24 }}>
        <div style={{ width: 52, height: 52, borderRadius: "50%", background: "rgba(254,186,6,0.1)", border: "2px solid #FEBA06", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 14px" }}>
          <svg width="22" height="22" fill="none" stroke="#FEBA06" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
          </svg>
        </div>
        <div style={{ fontSize: 20, fontWeight: 800, color: "#111827", marginBottom: 6, letterSpacing: "-0.02em" }}>Verify Your Email</div>
        <p style={{ fontSize: 13, color: "#6b7280", lineHeight: 1.55 }}>
          We've sent a verification code to<br />
          <strong style={{ color: "#111827" }}>{form.email}</strong>
        </p>
      </div>

      {/* OTP input + Verify button */}
      <div style={{ marginBottom: 8 }}>
        <label style={{ display: "block", fontSize: 11, fontWeight: 700, color: "#161412", letterSpacing: "0.07em", textTransform: "uppercase", marginBottom: 6 }}>
          Verification Code
        </label>
        <div style={{ display: "flex", gap: 8 }}>
          <input
            type="text"
            value={otpCode}
            onChange={e => { setOtpCode(e.target.value.replace(/\D/g, "").slice(0, 8)); setOtpError(""); }}
            placeholder="Enter code from email"
            disabled={otpVerified}
            style={{ flex: 1, border: `1.5px solid ${otpError ? "#dc2626" : otpVerified ? "#22c55e" : "#e5e7eb"}`, borderRadius: 8, padding: "10px 14px", fontSize: 14, color: "#111827", background: otpVerified ? "#f0fdf4" : "#f9fafb", outline: "none", fontFamily: "inherit", letterSpacing: "0.1em" }}
            onFocus={e => { if (!otpVerified) e.target.style.borderColor = "#FEBA06"; }}
            onBlur={e => { if (!otpVerified && !otpError) e.target.style.borderColor = "#e5e7eb"; }}
          />
          <button
            type="button"
            onClick={handleVerifyOtp}
            disabled={otpVerified || otpLoading || !otpCode.trim()}
            style={{ padding: "10px 20px", borderRadius: 8, border: "none", background: otpVerified ? "#22c55e" : "#FEBA06", color: otpVerified ? "#fff" : "#161412", fontWeight: 700, fontSize: 13, cursor: (otpVerified || otpLoading || !otpCode.trim()) ? "not-allowed" : "pointer", opacity: (!otpVerified && (otpLoading || !otpCode.trim())) ? 0.55 : 1, fontFamily: "inherit", whiteSpace: "nowrap", transition: "background 0.2s" }}
          >
            {otpVerified ? "✓ Verified" : otpLoading ? <Spinner /> : "Verify"}
          </button>
        </div>
        {otpError && <p style={{ margin: "5px 0 0", fontSize: 11, color: "#dc2626" }}>⚠ {otpError}</p>}
        {otpVerified && <p style={{ margin: "5px 0 0", fontSize: 11, color: "#22c55e", fontWeight: 600 }}>✓ Email verified successfully!</p>}

        {/* Resend code */}
        {!otpVerified && (
          <div style={{ marginTop: 10, display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: "#6b7280" }}>
            <span>Didn't receive the code?</span>
            <button
              type="button"
              onClick={handleResendOtp}
              disabled={resendCooldown > 0 || resendLoading}
              style={{
                background: "none",
                border: "none",
                padding: 0,
                fontSize: 12,
                fontWeight: 700,
                color: (resendCooldown > 0 || resendLoading) ? "#9ca3af" : "#FEBA06",
                cursor: (resendCooldown > 0 || resendLoading) ? "not-allowed" : "pointer",
                fontFamily: "inherit",
              }}
            >
              {resendLoading ? "Sending…" : resendCooldown > 0 ? `Resend code (${resendCooldown}s)` : "Resend code"}
            </button>
          </div>
        )}
      </div>

      {/* Actions */}
      <div style={{ display: "flex", gap: 10, marginTop: 22 }}>
        <button
          type="button"
          onClick={handleBack}
          disabled={loading}
          style={{ flex: "0 0 100px", padding: "11px 0", borderRadius: 8, border: "1.5px solid #e5e7eb", background: "#fff", color: "#374151", fontWeight: 600, fontSize: 13, cursor: loading ? "not-allowed" : "pointer", fontFamily: "inherit", opacity: loading ? 0.6 : 1 }}
        >
          ← Back
        </button>
        <button
          type="button"
          onClick={handleContinue}
          disabled={!otpVerified || loading}
          title={!otpVerified ? "Verify your email before continuing" : ""}
          style={{ flex: 1, padding: "11px 0", borderRadius: 8, border: "none", background: otpVerified ? "#FEBA06" : "#e5e7eb", color: otpVerified ? "#161412" : "#9ca3af", fontWeight: 800, fontSize: 13, letterSpacing: "0.06em", textTransform: "uppercase", cursor: otpVerified && !loading ? "pointer" : "not-allowed", fontFamily: "inherit", opacity: loading ? 0.7 : 1, transition: "background 0.2s, color 0.2s" }}
        >
          {loading ? <span style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 6 }}><Spinner />Creating…</span> : "Create Account"}
        </button>
      </div>
    </div>
  );

  // ════════════════════════════════════════════════════════════════════════
  // MODAL RENDER
  // ════════════════════════════════════════════════════════════════════════
  if (isModal) {
    return (
      <>
      <div style={{ background: "#ffffff", width: "100%", maxHeight: "88vh", overflowY: "auto" }}>
        {/* Dark header */}
        <div style={{ background: "#161412", padding: "14px 28px", display: "flex", alignItems: "center", position: "sticky", top: 0, zIndex: 2 }}>
          <img src={Logo} alt="Paving Market" style={{ height: 32, width: "auto" }} />
        </div>
        <div style={{ height: 3, background: "#FEBA06", width: "100%", position: "sticky", top: 62, zIndex: 2 }} />

        {/* Step indicator */}
        {step < 4 && <StepIndicator step={step} />}

        <div style={{ padding: "16px 28px 28px" }}>

          {/* ── Step 3: Verify Email ── */}
          {step === 3 && verifyStepContent}

          {/* ── Step 1: Account ── */}
          {step === 1 && (
            <>
              <div style={{ marginBottom: 18 }}>
                <div style={{ fontSize: 18, fontWeight: 800, color: "#161412", letterSpacing: "-0.02em" }}>Create Your Account</div>
                <p style={{ fontSize: 12, color: "#6b7280", marginTop: 4 }}>Enter your login credentials to get started</p>
              </div>

              {error && (
                <div style={{ background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 8, padding: "9px 12px", marginBottom: 14, fontSize: 12, color: "#dc2626" }}>
                  {error}
                </div>
              )}

              <form onSubmit={(e) => { e.preventDefault(); handleNext(); }} noValidate style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                {/* Email */}
                <FieldInput label="Email Address" name="email" value={form.email} onChange={ch}
                  onFocus={onFocus("email")} onBlur={onBlur("email")}
                  error={fieldErrors.email} placeholder="you@example.com" type="email" autoComplete="email" />

                {/* Password */}
                <div>
                  <label style={mLbl}>Password</label>
                  <div style={{ position: "relative" }}>
                    <input
                      type={showPwd ? "text" : "password"} name="password" value={form.password}
                      onChange={ch} autoComplete="new-password" placeholder="Min 8 chars, uppercase, number, symbol"
                      style={{
                        width: "100%", border: `1.5px solid ${fieldErrors.password ? "#dc2626" : "#e5e7eb"}`,
                        borderRadius: 8, padding: "10px 40px 10px 12px", fontSize: 13,
                        color: "#161412", background: fieldErrors.password ? "#fef2f2" : "#f9fafb",
                        outline: "none", boxSizing: "border-box", fontFamily: "inherit",
                      }}
                      onFocus={onFocus("password")} onBlur={onBlur("password")}
                    />
                    <button type="button" onClick={() => setShowPwd(!showPwd)}
                      style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", color: "#9ca3af", background: "none", border: "none", cursor: "pointer" }}>
                      <EyeIcon open={showPwd} />
                    </button>
                  </div>
                  <StrengthBar password={form.password} />
                  {fieldErrors.password && <p style={errMsg}>⚠ {fieldErrors.password}</p>}
                </div>

                {/* Confirm Password */}
                <div>
                  <label style={mLbl}>Confirm Password</label>
                  <div style={{ position: "relative" }}>
                    <input
                      type={showCfm ? "text" : "password"} name="confirmPassword" value={form.confirmPassword}
                      onChange={ch} autoComplete="new-password" placeholder="Repeat your password"
                      style={{
                        width: "100%", border: `1.5px solid ${fieldErrors.confirmPassword ? "#dc2626" : "#e5e7eb"}`,
                        borderRadius: 8, padding: "10px 40px 10px 12px", fontSize: 13,
                        color: "#161412", background: fieldErrors.confirmPassword ? "#fef2f2" : "#f9fafb",
                        outline: "none", boxSizing: "border-box", fontFamily: "inherit",
                      }}
                      onFocus={onFocus("confirmPassword")} onBlur={onBlur("confirmPassword")}
                    />
                    <button type="button" onClick={() => setShowCfm(!showCfm)}
                      style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", color: "#9ca3af", background: "none", border: "none", cursor: "pointer" }}>
                      <EyeIcon open={showCfm} />
                    </button>
                  </div>
                  {fieldErrors.confirmPassword && <p style={errMsg}>⚠ {fieldErrors.confirmPassword}</p>}
                </div>

                <button type="submit"
                  style={{ width: "100%", background: "#FEBA06", color: "#161412", fontWeight: 800, fontSize: 13, padding: "13px 0", borderRadius: 8, border: "none", cursor: "pointer", letterSpacing: "0.06em", textTransform: "uppercase", fontFamily: "inherit", marginTop: 4 }}>
                  Continue →
                </button>
              </form>

              <p style={{ textAlign: "center", marginTop: 18, fontSize: 13, color: "#6b7280" }}>
                Already have an account?{" "}
                <Link to="/login" onClick={toLogin} style={{ color: "#161412", fontWeight: 800, textDecoration: "none" }}>Sign in</Link>
              </p>
            </>
          )}

          {/* ── Step 2: Personal Info ── */}
          {step === 2 && (
            <>
              <div style={{ marginBottom: 18 }}>
                <div style={{ fontSize: 18, fontWeight: 800, color: "#161412", letterSpacing: "-0.02em" }}>Personal Information</div>
                <p style={{ fontSize: 12, color: "#6b7280", marginTop: 4 }}>Tell us a bit about yourself</p>
              </div>

              {error && (
                <div style={{ background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 8, padding: "9px 12px", marginBottom: 14, fontSize: 12, color: "#dc2626" }}>
                  {error}
                </div>
              )}

              <form onSubmit={handleSubmit} noValidate style={{ display: "flex", flexDirection: "column", gap: 13 }}>

                {/* Name row */}
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                  {[["firstName", "First Name"], ["lastName", "Last Name"]].map(([name, label]) => (
                    <FieldInput key={name} label={label} name={name} value={form[name]} onChange={ch}
                      onFocus={onFocus(name)} onBlur={onBlur(name)}
                      error={fieldErrors[name]} placeholder={label} />
                  ))}
                </div>

                {/* Primary Phone */}
                <div>
                  <label style={mLbl}>Phone Number</label>
                  <PhoneInput
                    country="us"
                    value={phone}
                    onChange={(value, country) => {
                      setPhone(value);
                      setDialCode(country.dialCode);
                      setFieldErrors(p => ({ ...p, phone: "" }));
                    }}
                    containerStyle={phoneContainerStyle}
                    inputStyle={phoneInputStyle(fieldErrors.phone)}
                    buttonStyle={phoneButtonStyle(fieldErrors.phone)}
                    inputProps={{ name: "phone", autoComplete: "tel" }}
                  />
                  {fieldErrors.phone && <p style={errMsg}>⚠ {fieldErrors.phone}</p>}
                </div>

                {/* Secondary Phone */}
                <div>
                  <label style={mLbl}>Secondary Phone <span style={optTag}>(optional)</span></label>
                  <PhoneInput
                    country="us"
                    value={secondaryPhone}
                    onChange={(value, country) => {
                      setSecondaryPhone(value);
                      setSecondaryDialCode(country.dialCode);
                      setFieldErrors(p => ({ ...p, secondaryPhone: "" }));
                    }}
                    containerStyle={phoneContainerStyle}
                    inputStyle={phoneInputStyle(fieldErrors.secondaryPhone)}
                    buttonStyle={phoneButtonStyle(fieldErrors.secondaryPhone)}
                    inputProps={{ name: "secondaryPhone", autoComplete: "tel" }}
                  />
                  {fieldErrors.secondaryPhone && <p style={errMsg}>⚠ {fieldErrors.secondaryPhone}</p>}
                </div>

                {/* Divider */}
                <div style={{ height: 1, background: "#f3f4f6", margin: "2px 0" }} />

                {/* Address */}
                <div>
                  <label style={mLbl}>Street Address<span style={optTag}> (optional)</span></label>
                  <GooglePlacesAutocomplete
                    apiKey={Google_map_Key}
                    onPlaceSelected={handlePlaceSelect}
                    onChange={(e) => { setForm(p => ({ ...p, address: e.target.value })); }}
                    options={{ types: ["address"] }}
                    value={form.address}
                    placeholder="123 Main St"
                    style={{
                      width: "100%", border: "1.5px solid #e5e7eb", borderRadius: 8,
                      padding: "10px 12px", fontSize: 13, color: "#161412",
                      background: "#f9fafb", outline: "none", boxSizing: "border-box", fontFamily: "inherit",
                    }}
                    onFocus={(e) => { e.target.style.borderColor = "#FEBA06"; e.target.style.background = "#fff"; }}
                    onBlur={(e) => { e.target.style.borderColor = "#e5e7eb"; e.target.style.background = "#f9fafb"; }}
                  />
                </div>

                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                  <FieldInput label="City" optional name="city" value={form.city} onChange={ch}
                    onFocus={onFocus("city")} onBlur={onBlur("city")} placeholder="New York" />
                  <FieldInput label="State" optional name="state" value={form.state} onChange={ch}
                    onFocus={onFocus("state")} onBlur={onBlur("state")} placeholder="NY" />
                </div>

                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                  <FieldInput label="Zip Code" optional name="zipCode" value={form.zipCode} onChange={ch}
                    onFocus={onFocus("zipCode")} onBlur={onBlur("zipCode")} placeholder="10001" />
                  <FieldInput label="Country" optional name="country" value={form.country} onChange={ch}
                    onFocus={onFocus("country")} onBlur={onBlur("country")} placeholder="United States" />
                </div>

                {/* Marketing checkbox */}
                {/* <label style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer", fontSize: 12, color: "#374151", marginTop: 2 }}>
                  <input type="checkbox" name="receiveMarketingEmails" checked={form.receiveMarketingEmails} onChange={ch}
                    style={{ width: 14, height: 14, accentColor: "#FEBA06", cursor: "pointer" }} />
                  Receive marketing emails and product updates
                </label> */}

                {/* Actions */}
                <div style={{ display: "flex", gap: 10, marginTop: 4 }}>
                  <button type="button" onClick={handleBack}
                    style={{ flex: "0 0 80px", background: "#f3f4f6", color: "#374151", fontWeight: 700, fontSize: 13, padding: "12px 0", borderRadius: 8, border: "none", cursor: "pointer", fontFamily: "inherit" }}>
                    ← Back
                  </button>
                  <button type="submit" disabled={loading}
                    style={{ flex: 1, background: "#FEBA06", color: "#161412", fontWeight: 800, fontSize: 13, padding: "12px 0", borderRadius: 8, border: "none", cursor: "pointer", letterSpacing: "0.06em", textTransform: "uppercase", fontFamily: "inherit", opacity: loading ? 0.7 : 1 }}>
                    {loading
                      ? <span style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}><Spinner />Creating…</span>
                      : "Create Account"}
                  </button>
                </div>
              </form>
            </>
          )}
        </div>
      </div>
      </>
    );
  }

  // ════════════════════════════════════════════════════════════════════════
  // FULL-PAGE RENDER
  // ════════════════════════════════════════════════════════════════════════

  const toLoginFP = toLogin;

  return (
    <>
    <div style={{
      minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center",
      padding: 24, position: "relative", overflow: "hidden",
      background: "linear-gradient(145deg,#161412 0%,#1f1b14 50%,#2a2319 100%)",
    }}>
      {/* Background blobs */}
      <div style={{ position: "absolute", width: 480, height: 480, borderRadius: "50%", background: "#FEBA06", opacity: 0.07, top: -160, right: -140, filter: "blur(85px)", pointerEvents: "none" }} />
      <div style={{ position: "absolute", width: 360, height: 360, borderRadius: "50%", background: "#FEBA06", opacity: 0.05, bottom: -110, left: -110, filter: "blur(70px)", pointerEvents: "none" }} />
      <div style={{ position: "absolute", width: 260, height: 260, borderRadius: "50%", border: "1px solid rgba(255,255,255,0.04)", top: "6%", left: "3%", pointerEvents: "none" }} />
      <div style={{ position: "absolute", inset: 0, backgroundImage: "radial-gradient(circle,rgba(255,255,255,0.022) 1px,transparent 1px)", backgroundSize: "36px 36px", pointerEvents: "none" }} />

      <div style={{
        position: "relative", zIndex: 10,
        width: "100%", maxWidth: 900,
        borderRadius: 24, overflow: "hidden", display: "flex",
        boxShadow: "0 40px 120px rgba(0,0,0,0.65)", minHeight: 580,
      }}>
        {/* Left panel */}
        <LeftPanel step={step} navigate={navigate} toLogin={toLoginFP} />

        {/* Right panel */}
        <div style={{ flex: 1, background: "#ffffff", display: "flex", flexDirection: "column", overflow: "hidden" }}>

          {/* Top bar */}
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "11px 28px", borderBottom: "1px solid #f3f4f6", flexShrink: 0 }}>
            {step > 1 && step < 4 ? (
              <button onClick={handleBack}
                style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 12, color: "#9ca3af", fontWeight: 600, background: "none", border: "none", cursor: "pointer" }}>
                <svg width="13" height="13" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
                Back
              </button>
            ) : (
              <button onClick={() => navigate("/")}
                style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 12, color: "#9ca3af", fontWeight: 500, background: "none", border: "none", cursor: "pointer" }}>
                <svg width="12" height="12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
                Home Page
              </button>
            )}
            <span style={{ fontSize: 12, color: "#9ca3af", fontWeight: 500 }}>
              Have an account?{" "}
              <Link to="/login" onClick={toLoginFP} style={{ fontWeight: 700, color: "#FEBA06", textDecoration: "none" }}>Sign in</Link>
            </span>
          </div>

          {/* Step indicator */}
          {step < 4 && (
            <div style={{ padding: "0 28px", borderBottom: "1px solid #f3f4f6", flexShrink: 0 }}>
              <StepIndicator step={step} />
            </div>
          )}

          {/* Form area */}
          <div style={{ flex: 1, padding: "20px 28px 24px", overflow: "auto" }}>

            {/* ── Step 1: Account ── */}
            {step === 1 && (
              <>
                <div style={{ marginBottom: 20 }}>
                  <div style={{ fontSize: 22, fontWeight: 800, color: "#111827", letterSpacing: "-0.02em" }}>Create your account</div>
                  <p style={{ fontSize: 12, color: "#6b7280", marginTop: 4 }}>Start with your login credentials</p>
                </div>

                {error && (
                  <div style={{ display: "flex", alignItems: "flex-start", gap: 8, background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 8, padding: "9px 12px", marginBottom: 16, fontSize: 12, color: "#dc2626" }}>
                    <svg style={{ width: 13, height: 13, marginTop: 1, flexShrink: 0 }} fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                    {error}
                  </div>
                )}

                <form onSubmit={(e) => { e.preventDefault(); handleNext(); }} noValidate style={{ display: "flex", flexDirection: "column", gap: 16, maxWidth: 400 }}>
                  {/* Email */}
                  <div>
                    <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "#111827", marginBottom: 5 }}>
                      Email Address <span style={{ color: "#ef4444" }}>*</span>
                    </label>
                    <input type="email" name="email" value={form.email} onChange={ch}
                      autoComplete="email" placeholder="you@example.com"
                      style={{ width: "100%", border: `1.5px solid ${fieldErrors.email ? "#ef4444" : "#e5e7eb"}`, borderRadius: 8, padding: "10px 14px", fontSize: 13, color: "#111827", background: fieldErrors.email ? "#fef2f2" : "#f9fafb", outline: "none", boxSizing: "border-box", fontFamily: "inherit" }}
                      onFocus={e => { e.target.style.borderColor = fieldErrors.email ? "#ef4444" : "#FEBA06"; e.target.style.background = "#fff"; }}
                      onBlur={e => { e.target.style.borderColor = fieldErrors.email ? "#ef4444" : "#e5e7eb"; e.target.style.background = fieldErrors.email ? "#fef2f2" : "#f9fafb"; }} />
                    {fieldErrors.email && <p style={{ margin: "4px 0 0", fontSize: 11, color: "#ef4444" }}>⚠ {fieldErrors.email}</p>}
                  </div>

                  {/* Password */}
                  <div>
                    <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "#111827", marginBottom: 5 }}>
                      Password <span style={{ color: "#ef4444" }}>*</span>
                    </label>
                    <div style={{ position: "relative" }}>
                      <input type={showPwd ? "text" : "password"} name="password" value={form.password} onChange={ch}
                        autoComplete="new-password" placeholder="Min 8 chars, uppercase, number, symbol"
                        style={{ width: "100%", border: `1.5px solid ${fieldErrors.password ? "#ef4444" : "#e5e7eb"}`, borderRadius: 8, padding: "10px 40px 10px 14px", fontSize: 13, color: "#111827", background: fieldErrors.password ? "#fef2f2" : "#f9fafb", outline: "none", boxSizing: "border-box", fontFamily: "inherit" }}
                        onFocus={e => { e.target.style.borderColor = "#FEBA06"; e.target.style.background = "#fff"; }}
                        onBlur={e => { e.target.style.borderColor = fieldErrors.password ? "#ef4444" : "#e5e7eb"; e.target.style.background = fieldErrors.password ? "#fef2f2" : "#f9fafb"; }} />
                      <button type="button" onClick={() => setShowPwd(!showPwd)}
                        style={{ position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)", color: "#d1d5db", background: "none", border: "none", cursor: "pointer" }}>
                        <EyeIcon open={showPwd} />
                      </button>
                    </div>
                    <StrengthBar password={form.password} />
                    {fieldErrors.password && <p style={{ margin: "4px 0 0", fontSize: 11, color: "#ef4444" }}>⚠ {fieldErrors.password}</p>}
                  </div>

                  {/* Confirm Password */}
                  <div>
                    <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "#111827", marginBottom: 5 }}>
                      Confirm Password <span style={{ color: "#ef4444" }}>*</span>
                    </label>
                    <div style={{ position: "relative" }}>
                      <input type={showCfm ? "text" : "password"} name="confirmPassword" value={form.confirmPassword} onChange={ch}
                        autoComplete="new-password" placeholder="Repeat your password"
                        style={{ width: "100%", border: `1.5px solid ${fieldErrors.confirmPassword ? "#ef4444" : "#e5e7eb"}`, borderRadius: 8, padding: "10px 40px 10px 14px", fontSize: 13, color: "#111827", background: fieldErrors.confirmPassword ? "#fef2f2" : "#f9fafb", outline: "none", boxSizing: "border-box", fontFamily: "inherit" }}
                        onFocus={e => { e.target.style.borderColor = "#FEBA06"; e.target.style.background = "#fff"; }}
                        onBlur={e => { e.target.style.borderColor = fieldErrors.confirmPassword ? "#ef4444" : "#e5e7eb"; e.target.style.background = fieldErrors.confirmPassword ? "#fef2f2" : "#f9fafb"; }} />
                      <button type="button" onClick={() => setShowCfm(!showCfm)}
                        style={{ position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)", color: "#d1d5db", background: "none", border: "none", cursor: "pointer" }}>
                        <EyeIcon open={showCfm} />
                      </button>
                    </div>
                    {fieldErrors.confirmPassword && <p style={{ margin: "4px 0 0", fontSize: 11, color: "#ef4444" }}>⚠ {fieldErrors.confirmPassword}</p>}
                  </div>

                  <button type="submit"
                    style={{ width: "100%", background: "#FEBA06", color: "#111827", fontWeight: 700, fontSize: 14, padding: "12px 0", borderRadius: 10, border: "none", cursor: "pointer", letterSpacing: "0.03em", boxShadow: "0 6px 20px rgba(254,186,6,0.3)", fontFamily: "inherit", marginTop: 4 }}>
                    Continue →
                  </button>
                  <p style={{ textAlign: "center", fontSize: 12, color: "#9ca3af", marginTop: 4 }}>
                    Already have an account?{" "}
                    <Link to="/login" onClick={toLoginFP} style={{ fontWeight: 700, color: "#1f2937", textDecoration: "none" }}>Log in</Link>
                  </p>
                </form>
              </>
            )}

            {/* ── Step 2: Personal Info ── */}
            {step === 2 && (
              <>
                <div style={{ marginBottom: 18 }}>
                  <div style={{ fontSize: 22, fontWeight: 800, color: "#111827", letterSpacing: "-0.02em" }}>Personal Information</div>
                  <p style={{ fontSize: 12, color: "#6b7280", marginTop: 4 }}>Complete your profile</p>
                </div>

                {error && (
                  <div style={{ display: "flex", alignItems: "flex-start", gap: 8, background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 8, padding: "9px 12px", marginBottom: 16, fontSize: 12, color: "#dc2626" }}>
                    <svg style={{ width: 13, height: 13, marginTop: 1, flexShrink: 0 }} fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                    {error}
                  </div>
                )}

                <form onSubmit={handleSubmit} noValidate>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>

                    {/* Left column */}
                    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                      {/* Name */}
                      {[["firstName", "First Name"], ["lastName", "Last Name"]].map(([name, label]) => (
                        <div key={name}>
                          <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "#111827", marginBottom: 5 }}>
                            {label} <span style={{ color: "#ef4444" }}>*</span>
                          </label>
                          <input type="text" name={name} value={form[name]} onChange={ch} placeholder={label}
                            style={{ width: "100%", border: `1.5px solid ${fieldErrors[name] ? "#ef4444" : "#e5e7eb"}`, borderRadius: 8, padding: "10px 14px", fontSize: 13, color: "#111827", background: fieldErrors[name] ? "#fef2f2" : "#f9fafb", outline: "none", boxSizing: "border-box", fontFamily: "inherit" }}
                            onFocus={e => { e.target.style.borderColor = "#FEBA06"; e.target.style.background = "#fff"; }}
                            onBlur={e => { e.target.style.borderColor = fieldErrors[name] ? "#ef4444" : "#e5e7eb"; e.target.style.background = fieldErrors[name] ? "#fef2f2" : "#f9fafb"; }} />
                          {fieldErrors[name] && <p style={{ margin: "4px 0 0", fontSize: 11, color: "#ef4444" }}>⚠ {fieldErrors[name]}</p>}
                        </div>
                      ))}

                      {/* Primary phone */}
                      <div>
                        <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "#111827", marginBottom: 5 }}>
                          Phone Number <span style={{ color: "#ef4444" }}>*</span>
                        </label>
                        <PhoneInput
                          country="us"
                          value={phone}
                          onChange={(value, country) => {
                            setPhone(value);
                            setDialCode(country.dialCode);
                            setFieldErrors(p => ({ ...p, phone: "" }));
                          }}
                          containerStyle={phoneContainerStyle}
                          inputStyle={{ ...phoneInputStyle(fieldErrors.phone), fontSize: 13 }}
                          buttonStyle={phoneButtonStyle(fieldErrors.phone)}
                          inputProps={{ name: "phone", autoComplete: "tel" }}
                        />
                        {fieldErrors.phone && <p style={{ margin: "4px 0 0", fontSize: 11, color: "#ef4444" }}>⚠ {fieldErrors.phone}</p>}
                      </div>

                      {/* Secondary phone */}
                      <div>
                        <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "#111827", marginBottom: 5 }}>
                          Secondary Phone <span style={{ fontSize: 11, color: "#9ca3af", fontWeight: 400 }}>(optional)</span>
                        </label>
                        <PhoneInput
                          country="us"
                          value={secondaryPhone}
                          onChange={(value, country) => {
                            setSecondaryPhone(value);
                            setSecondaryDialCode(country.dialCode);
                            setFieldErrors(p => ({ ...p, secondaryPhone: "" }));
                          }}
                          containerStyle={phoneContainerStyle}
                          inputStyle={{ ...phoneInputStyle(fieldErrors.secondaryPhone), fontSize: 13 }}
                          buttonStyle={phoneButtonStyle(fieldErrors.secondaryPhone)}
                          inputProps={{ name: "secondaryPhone", autoComplete: "tel" }}
                        />
                        {fieldErrors.secondaryPhone && <p style={{ margin: "4px 0 0", fontSize: 11, color: "#ef4444" }}>⚠ {fieldErrors.secondaryPhone}</p>}
                      </div>

                      {/* Marketing checkbox */}
                      {/* <label style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer", fontSize: 12, color: "#374151" }}>
                        <input type="checkbox" name="receiveMarketingEmails" checked={form.receiveMarketingEmails} onChange={ch}
                          style={{ width: 14, height: 14, accentColor: "#FEBA06", cursor: "pointer" }} />
                        Receive marketing emails and product updates
                      </label> */}
                    </div>

                    {/* Right column – address */}
                    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                      {/* Street Address — Google Places Autocomplete */}
                      <div>
                        <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "#111827", marginBottom: 5 }}>
                          Street Address <span style={{ fontSize: 11, color: "#9ca3af", fontWeight: 400 }}>(optional)</span>
                        </label>
                        <GooglePlacesAutocomplete
                          apiKey={Google_map_Key}
                          onPlaceSelected={handlePlaceSelect}
                          onChange={(e) => { setForm(p => ({ ...p, address: e.target.value })); }}
                          options={{ types: ["address"] }}
                          value={form.address}
                          placeholder="123 Main St"
                          style={{ width: "100%", border: "1.5px solid #e5e7eb", borderRadius: 8, padding: "10px 14px", fontSize: 13, color: "#111827", background: "#f9fafb", outline: "none", boxSizing: "border-box", fontFamily: "inherit" }}
                          onFocus={(e) => { e.target.style.borderColor = "#FEBA06"; e.target.style.background = "#fff"; }}
                          onBlur={(e) => { e.target.style.borderColor = "#e5e7eb"; e.target.style.background = "#f9fafb"; }}
                        />
                      </div>
                      {/* Remaining address fields — editable manually or auto-filled */}
                      {[
                        ["city",    "City",            "New York"],
                        ["state",   "State / Province", "NY"],
                        ["zipCode", "Zip / Postal Code", "10001"],
                        ["country", "Country",          "United States"],
                      ].map(([name, label, placeholder]) => (
                        <div key={name}>
                          <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "#111827", marginBottom: 5 }}>
                            {label} <span style={{ fontSize: 11, color: "#9ca3af", fontWeight: 400 }}>(optional)</span>
                          </label>
                          <input type="text" name={name} value={form[name]} onChange={ch} placeholder={placeholder}
                            style={{ width: "100%", border: "1.5px solid #e5e7eb", borderRadius: 8, padding: "10px 14px", fontSize: 13, color: "#111827", background: "#f9fafb", outline: "none", boxSizing: "border-box", fontFamily: "inherit" }}
                            onFocus={e => { e.target.style.borderColor = "#FEBA06"; e.target.style.background = "#fff"; }}
                            onBlur={e => { e.target.style.borderColor = "#e5e7eb"; e.target.style.background = "#f9fafb"; }} />
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Submit row */}
                  <div style={{ display: "flex", gap: 12, marginTop: 22 }}>
                    <button type="button" onClick={handleBack}
                      style={{ flex: "0 0 100px", background: "#f3f4f6", color: "#374151", fontWeight: 700, fontSize: 13, padding: "12px 0", borderRadius: 10, border: "1px solid #e5e7eb", cursor: "pointer", fontFamily: "inherit" }}>
                      ← Back
                    </button>
                    <button type="submit" disabled={loading}
                      style={{ flex: 1, background: "#FEBA06", color: "#111827", fontWeight: 700, fontSize: 14, padding: "12px 0", borderRadius: 10, border: "none", cursor: "pointer", letterSpacing: "0.03em", boxShadow: "0 6px 20px rgba(254,186,6,0.3)", fontFamily: "inherit", opacity: loading ? 0.6 : 1 }}>
                      {loading
                        ? <span style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}><Spinner />Creating account…</span>
                        : "Create Account"}
                    </button>
                  </div>
                </form>
              </>
            )}

            {/* ── Step 3: Verify Email ── */}
            {step === 3 && (
              <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "center", paddingTop: 8 }}>
                {verifyStepContent}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
    </>
  );
};

export default Register;
